import { Box, lighten, useTheme } from '@material-ui/core';
import { ResponsiveLine } from '@nivo/line';
import React from 'react';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { DomainPerformanceAnalysisGq } from '../../../../graphql/projects/queries/analysis/useGetCompetitiveRankingStrategyGq';
import { ChartProps } from '../../AnalysisList';
import { internationalText } from '../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../DataTimedOut';
import NotEnoughData from '../NotEnoughData';

export interface CompetitiveLandscapeProps extends ChartProps { }

export const CompetitiveLandscape: React.FC<CompetitiveLandscapeProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
  skipAnimation,
  emptyOnFail,
}) => {
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showDomainIds: true,
          showTagGroupId: true,
          showTagId: true,
          showCompetitorColor: true,
        },
      })),
    []
  );

  // Data fetching
  const dataQuery = projectsApiService.queries.analysis.useGetCompetitiveRankingStrategy({
    projectId: project.id,
    domainIds: analysisFilterState?.domainIds,
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
  });

  const domainAnalyses: DomainPerformanceAnalysisGq[] =
    dataQuery?.result?.domainPerformanceAnalyses ?? [];

  const domainResults = domainAnalyses.map((d): any => ({
    id: d.domain.id,
    type: d.domain.type,
    data: [{ x: d.keywordCount, y: d.traffic, id: d.domain.name }],
  }));

  // Styling
  const theme = useTheme();
  const chartTheme = {
    theme: {
      fontSize: 14,
      color: theme.palette.info.main,
      labels: {
        text: {
          fontSize: 14,
          color: theme.palette.info.main,
        },
      },
    },
  };

  const localSkipAnimation = sessionStorage.getItem('skip_animation') || skipAnimation; // Setting for skipping animations when screenshotting

  if (didQueryTimeout(dataQuery?.query)) {
    return emptyOnFail ? null : <DataTimedOut />;
  }

  if (dataQuery?.query?.loading) {
    return <AnalysisChartLoading />;
  }

  if (!domainAnalyses.length) {
    return emptyOnFail ? null : <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <div className="h-[500px]">
        <ResponsiveLine
          animate={localSkipAnimation ? false : true}
          theme={chartTheme.theme}
          data={domainResults}
          margin={{ top: 50, right: 65, bottom: 55, left: 65 }}
          xScale={{ type: 'linear', min: 0, max: 'auto' }}
          yScale={{ type: 'linear', min: 0, max: 'auto' }}
          colors={(v: any) => {
            if (v.type === DomainTypeGq.PROJECT) {
              return palette.secondaryScale[1];
            } else if (
              v.type === DomainTypeGq.COMPETITOR ||
              (v.type === DomainTypeGq.AUTOMATIC && !analysisFilterState?.competitorColor)
            ) {
              return palette.primaryScale[2];
            } else {
              return palette.primaryScale[1];
            }
          }}
          axisBottom={{
            format: (e) => Math.floor(e) === e && e,
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
            legend: internationalText.chart.competitive_landscape.kw_label[language],
            legendPosition: 'middle',
            legendOffset: 46,
          }}
          axisLeft={{
            format: '~s',
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
            legend: internationalText.chart.competitive_landscape.traffic_label[language],
            legendPosition: 'middle',
            legendOffset: -60,
          }}
          enablePointLabel={true}
          pointSize={16}
          pointLabel={function (d: any) {
            return d.id;
          }}
          pointLabelYOffset={-18}
        />
      </div>
    </div>
  );
};

export default CompetitiveLandscape;
